"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { ArrowUpRight, BookOpen, Github, Package } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { projects } from "@/data/projects";
import PasswordGate from "@/components/PasswordGate";
import ProjectHero from "@/components/ProjectHero";
import ProjectHeroImage from "@/components/ProjectHeroImage";
import ProjectOverview from "@/components/ProjectOverview";
import NumberedSection from "@/components/NumberedSection";
import ProjectOutput from "@/components/ProjectOutput";
import NextProjectFooter from "@/components/NextProjectFooter";
import ProjectSidebar, { SidebarSection } from "@/components/ProjectSidebar";
import AnimaliaPlayground from "@/components/animalia/AnimaliaPlayground";
import AnimaliaIconCarousel from "@/components/animalia/AnimaliaIconCarousel";
import { useAnalytics } from "@/hooks/useAnalytics";

const AnimaliaTokenGraph = dynamic(() => import("@/components/animalia/AnimaliaTokenGraph"), { ssr: false });

gsap.registerPlugin(ScrollTrigger);

const CHECK_IT_OUT_LINKS = [
  {
    title: "Documentation",
    url: "https://doc.animaliads.io/",
    displayUrl: "doc.animaliads.io",
    icon: BookOpen,
  },
  {
    title: "Repository",
    url: "https://github.com/animaliads",
    displayUrl: "github.com/animaliads",
    icon: Github,
  },
  {
    title: "Icon Library",
    url: "https://www.npmjs.com/package/@animaliads/animalia-icon",
    displayUrl: "npmjs.com/package/@animaliads/animalia-icon",
    icon: Package,
  },
];

const BIG_TAKEAWAYS = [
  {
    number: "01",
    title: "Flexibility Through Abstraction",
    content:
      "From token architecture to the promotion of core components across multiple teams, the system was built on a simple belief: flexibility comes from the ability to adapt to diverse contexts with consistency — not from rigid standards.",
  },
  {
    number: "02",
    title: "Accessibility as a Foundation",
    content:
      "Accessibility was a core principle from day one — not a guideline bolted on later. WCAG AAA was the opening baseline; WCAG 2.2 became the operating standard. Every component was built to naturally meet diverse user needs.",
  },
  {
    number: "03",
    title: "A Unified Platform Accelerates M&A",
    content:
      "The design system became a transformation accelerator, letting the company absorb acquisitions without compromising quality or cohesion. When agility and alignment are vital, a unified platform is essential.",
  },
];

const APPROACH_COLUMNS = [
  {
    title: "Framework-agnostic",
    content:
      "The system had to work regardless of the frontend stack a team had in place. A PoC was validated across 8 cross-functional teams — designers, developers, and product owners — and proved that components could be delivered through an invisible integration layer: teams kept calling components the way they always had, while the system provided the design contract underneath. No migration tax. No change in habits.",
  },
  {
    title: "White-label design system",
    content:
      "Full visual flexibility — with non-negotiables. A WCAG AAA baseline was championed as a team decision, not mandated from above. Teams could customize visual identity freely, but the system held firm on what couldn't be negotiated: contrast ratios, focus states, touch targets, motion sensitivity, and screen reader compatibility.",
  },
];

const AnimaliaContent = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const takeawaysRef = useRef<HTMLDivElement>(null);
  const checkItOutRef = useRef<HTMLDivElement>(null);
  const componentSectionRef = useRef<HTMLElement>(null);
  const componentLabelRef = useRef<HTMLParagraphElement>(null);
  const componentShowcaseRef = useRef<HTMLDivElement>(null);
  const componentStickyRef = useRef<HTMLDivElement>(null);
  const componentLightRef = useRef<HTMLDivElement>(null);
  const componentDarkRef = useRef<HTMLDivElement>(null);
  const statRef = useRef<HTMLSpanElement>(null);
  const { trackProject } = useAnalytics();

  const projectData = projects.find((p) => p.id === "animalia");

  const currentIndex = projects.findIndex((p) => p.id === "animalia");
  const nextProject =
    currentIndex >= 0 && currentIndex < projects.length - 1
      ? projects[currentIndex + 1]
      : projects[0];

  useEffect(() => {
    if (!projectData) return;
    trackProject("view_page", {
      id: projectData.id,
      title: projectData.title,
      category: projectData.category,
      year: projectData.year,
      featured: projectData.featured,
    });
  }, [projectData, trackProject]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Custom sections entrance
      gsap.fromTo(
        ".animalia-section",
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          // Kept short: these reveals fire at every section boundary, so they
          // are felt often rather than once.
          duration: 0.4,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".animalia-section",
            start: "top 85%",
          },
        },
      );

      // Big takeaway cards
      gsap.fromTo(
        ".takeaway-card",
        { y: 32, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.4,
          stagger: 0.12,
          ease: "power2.out",
          scrollTrigger: {
            trigger: takeawaysRef.current,
            start: "top 80%",
          },
        },
      );

      // Core-problem stat. A one-off, first-time moment, so it earns a count-up
      // where the repeated section reveals do not.
      if (statRef.current) {
        const node = statRef.current;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          node.textContent = "57%";
        } else {
          const counter = { value: 0 };
          node.textContent = "0%";
          gsap.to(counter, {
            value: 57,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: node,
              start: "top 85%",
              once: true,
            },
            onUpdate: () => {
              node.textContent = `${Math.round(counter.value)}%`;
            },
          });
        }
      }

      // External resource links
      gsap.fromTo(
        ".check-it-out-link",
        { x: -16, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.4,
          stagger: 0.08,
          ease: "power2.out",
          scrollTrigger: {
            trigger: checkItOutRef.current,
            start: "top 85%",
          },
        },
      );

      if (
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
        componentSectionRef.current &&
        componentLabelRef.current &&
        componentShowcaseRef.current &&
        componentStickyRef.current &&
        componentLightRef.current &&
        componentDarkRef.current
      ) {
        gsap
          .timeline({
            scrollTrigger: {
              trigger: componentShowcaseRef.current,
              start: "top top+=64",
              end: "bottom bottom",
              scrub: true,
              pin: componentStickyRef.current,
              pinSpacing: false,
              anticipatePin: 1,
              invalidateOnRefresh: true,
            },
          })
          .to(componentLightRef.current, { opacity: 0, ease: "none" }, 0)
          .to(componentDarkRef.current, { opacity: 1, ease: "none" }, 0)
          .to(
            componentSectionRef.current,
            {
              backgroundColor: "#000000",
              borderColor: "#262626",
              ease: "none",
            },
            0,
          )
          .to(componentLabelRef.current, { color: "#d4d4d4", ease: "none" }, 0);
      }
    }, containerRef);

    // The token explorer and other late content change the page height after
    // ScrollTrigger has measured it, which starts the component pin early.
    let refreshTimer = 0;
    let lastHeight = 0;
    const resizeObserver = new ResizeObserver(([entry]) => {
      const height = Math.round(entry.contentRect.height);
      if (height === lastHeight) return;
      lastHeight = height;
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 150);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      window.clearTimeout(refreshTimer);
      ctx.revert();
    };
  }, []);

  if (!projectData) {
    return (
      <div className="min-h-screen bg-[#fafcfd] text-[#111111]">
        Project not found
      </div>
    );
  }

  // Build sidebar sections dynamically
  const sidebarSections: SidebarSection[] = [
    ...(projectData.overview ? [{ id: "overview", label: "Overview" }] : []),
    { id: "core-problem", label: "Core Problem" },
    ...(projectData.quote ? [{ id: "main-goal", label: "Main Goal" }] : []),
    { id: "approach", label: "Approach" },
    ...(projectData.sections
      ? projectData.sections.map((s) => ({
          id: `section-${s.number}`,
          label: s.title,
        }))
      : []),
    { id: "component-showcase", label: "Components" },
    { id: "delivery", label: "Delivery" },
    { id: "takeaways", label: "Takeaways" },
    ...(projectData.output ? [{ id: "output", label: "Output" }] : []),
    { id: "check-it-out", label: "Check it out" },
  ];

  return (
    <div ref={containerRef} className="bg-[#fafcfd] text-[#111111]">
      <ProjectSidebar sections={sidebarSections} />

      {/* Hero */}
      <section className="relative min-h-fit md:h-[70vh] overflow-hidden bg-[#fafcfd]">
        <div className="flex items-center justify-center md:h-full">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <ProjectHero
              title={projectData.title}
              subtitle={projectData.subtitle}
              category={projectData.category}
              year={projectData.year}
              featured={projectData.featured}
              link={projectData.link}
              infoBlocks={projectData.infoBlocks || []}
            />
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <ProjectHeroImage
        image={projectData.heroImage}
        video={projectData.heroVideo}
        alt={projectData.title + " Hero Image"}
      />

      {/* Overview */}
      {projectData.overview && (
        <ProjectOverview overview={projectData.overview} id="overview" />
      )}

      {/* Overview presentation */}
      <section className="px-4 pb-24">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-lg bg-[#f4f4f5]">
          <Image
            src="/images/animalia/overview-presentation.png"
            alt="Amilcar presenting a design workshop to the product team"
            width={1000}
            height={821}
            sizes="(max-width: 1280px) calc(100vw - 32px), 1280px"
            className="h-auto w-full grayscale"
          />
        </div>
      </section>

      {/* ── Core Problem (57% stat) ── */}
      <section
        id="core-problem"
        className="animalia-section py-24 border-t border-[#e8e8e8]"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <p className="text-sm uppercase tracking-[0.15em] text-[#6f6f6f] mb-12 font-pp-neuebit">
              core problem
            </p>

            <div className="lg:w-[57%]">
              <span
                ref={statRef}
                aria-hidden="true"
                className="font-pp-acma text-[#2D0456] text-[clamp(96px,14vw,176px)] leading-none font-semibold tabular-nums"
              >
                57%
              </span>
              <span className="sr-only">57%</span>
              <p className="mt-6 text-lg text-[#2D0456] font-neue-regrade-variable leading-relaxed">
                of teams worked outside that supported stack.
              </p>
            </div>

            <p className="mt-16 max-w-3xl text-lg text-[#6b6b6b] font-neue-regrade-variable leading-relaxed">
              Product teams were maintaining separate component libraries
              because the existing system only supported one frontend
              technology. This duplicated work and created an inconsistent
              experience across TOTVS products.
            </p>
          </div>
        </div>
      </section>

      {/* Quote / Main Goal */}
      {projectData.quote && (
        <section
          id="main-goal"
          className="animalia-section py-24 border-t border-[#e8e8e8]"
        >
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p className="text-sm uppercase tracking-[0.15em] text-[#6f6f6f] mb-6 font-pp-neuebit">
              main goal
            </p>
            <p className="text-[#111111] font-pp-acma text-[clamp(24px,4vw,48px)] font-semibold leading-[1.2] lowercase">
              {projectData.quote.text}
            </p>
          </div>
        </section>
      )}

      {/* ── Approach (framework-agnostic + white-label) ── */}
      <section
        id="approach"
        className="animalia-section py-24 border-t border-[#e8e8e8]"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <p className="text-sm uppercase tracking-[0.15em] text-[#6f6f6f] mb-12 font-pp-neuebit">
              approach
            </p>

            <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
              {APPROACH_COLUMNS.map((col) => (
                <div key={col.title}>
                  <h3 className="text-2xl md:text-3xl font-pp-acma font-bold text-[#111111] mb-6 leading-tight">
                    {col.title}
                  </h3>
                  <p className="text-base text-[#6b6b6b] font-neue-regrade-variable leading-relaxed">
                    {col.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Numbered Sections (System Evolution Phases) ── */}
      {projectData.sections &&
        projectData.sections.map((section, index) => (
          <div key={index} className="animalia-section">
            <NumberedSection
              number={section.number}
              title={section.title}
              content={section.content}
              largeImage={section.largeImage}
              smallImages={section.smallImages}
              contentAfter={section.contentAfter}
              contentAfterExtra={section.contentAfterExtra}
              contentAfterDetailed={section.contentAfterDetailed}
              afterMediaImage={section.afterMediaImage}
              afterMedia={section.number === "02" ? <AnimaliaTokenGraph /> : undefined}
              contentAfterImage={section.contentAfterImage}
              id={`section-${section.number}`}
              media={
                section.number === "01" ? (
                  <AnimaliaPlayground />
                ) : section.number === "02" ? (
                  <AnimaliaIconCarousel />
                ) : undefined
              }
            />
          </div>
        ))}

      {/* ── Component Showcase ── */}
      <section
        ref={componentSectionRef}
        id="component-showcase"
        className="bg-white py-24 border-t border-[#e8e8e8]"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <p
              ref={componentLabelRef}
              className="text-sm uppercase tracking-[0.15em] text-[#525252] mb-12 font-pp-neuebit"
            >
              component ecosystem
            </p>

            <div
              ref={componentShowcaseRef}
              className="relative h-[180svh] md:h-[200vh] motion-reduce:h-auto"
            >
              <div
                ref={componentStickyRef}
                className="relative flex h-[calc(100svh-4rem)] items-center motion-reduce:h-auto"
              >
                <div className="grid h-full w-full place-items-center py-6 md:py-8 motion-reduce:flex motion-reduce:h-auto motion-reduce:flex-col motion-reduce:gap-8 motion-reduce:py-0">
                  <div
                    ref={componentLightRef}
                    className="col-start-1 row-start-1 flex h-full w-full items-center justify-center motion-reduce:relative motion-reduce:left-1/2 motion-reduce:h-auto motion-reduce:w-screen motion-reduce:-translate-x-1/2 motion-reduce:bg-white motion-reduce:px-4 motion-reduce:py-8"
                  >
                    <div className="flex h-full w-full items-center justify-center motion-reduce:mx-auto motion-reduce:h-auto motion-reduce:max-w-7xl">
                      <Image
                        src="/images/animalia/components.png"
                        alt="Animalia components — light mode"
                        width={1800}
                        height={1012}
                        loading="eager"
                        sizes="(min-width: 1280px) 1280px, calc(100vw - 2rem)"
                        className="h-auto max-h-[calc(100svh-8rem)] w-auto max-w-full rounded-lg motion-reduce:max-h-none motion-reduce:w-full"
                      />
                    </div>
                  </div>

                  <div
                    ref={componentDarkRef}
                    className="col-start-1 row-start-1 flex h-full w-full items-center justify-center opacity-0 motion-reduce:relative motion-reduce:left-1/2 motion-reduce:h-auto motion-reduce:w-screen motion-reduce:-translate-x-1/2 motion-reduce:bg-black motion-reduce:px-4 motion-reduce:py-8 motion-reduce:opacity-100"
                  >
                    <div className="flex h-full w-full items-center justify-center motion-reduce:mx-auto motion-reduce:h-auto motion-reduce:max-w-7xl">
                      <Image
                        src="/images/animalia/motion.png"
                        alt="Animalia components — dark mode"
                        width={1800}
                        height={1012}
                        loading="eager"
                        sizes="(min-width: 1280px) 1280px, calc(100vw - 2rem)"
                        className="h-auto max-h-[calc(100svh-8rem)] w-auto max-w-full rounded-lg motion-reduce:max-h-none motion-reduce:w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Delivery a Basic Login comparison ── */}
      <section
        id="delivery"
        className="animalia-section py-24 border-t border-[#e8e8e8]"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <p className="text-sm uppercase tracking-[0.15em] text-[#6f6f6f] mb-12 font-pp-neuebit">
              outcomes — deliver a basic login
            </p>

            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <ul className="text-base text-[#6b6b6b] font-neue-regrade-variable leading-relaxed space-y-2 mb-12 list-disc pl-5">
                  <li>Simple form with email / password fields</li>
                  <li>Basic HTML / CSS styling</li>
                  <li>Form validation (required fields, email format)</li>
                  <li>Submit handler with console output</li>
                  <li>Basic responsive design</li>
                </ul>

                <div className="mb-10">
                  <p className="font-pp-acma text-[#6f6f6f] line-through text-[clamp(40px,6vw,72px)] leading-none font-semibold">
                    2–3 hours
                  </p>
                  <p className="mt-3 text-sm uppercase tracking-[0.15em] text-[#6f6f6f] font-pp-neuebit">
                    other generic stack
                  </p>
                </div>

                <div>
                  <p className="font-pp-acma text-[#2D0456] text-[clamp(48px,7vw,88px)] leading-none font-semibold">
                    35 min
                  </p>
                  <p className="mt-3 text-sm uppercase tracking-[0.15em] text-[#2D0456] font-pp-neuebit">
                    with Animalia DS
                  </p>
                </div>
              </div>

              <div className="relative aspect-[3/4] bg-[#f4f4f5] overflow-hidden rounded-lg max-w-md mx-auto">
                <Image
                  src="/images/animalia/delivery-phone.png"
                  alt="iPhone mockup — login built with Animalia DS"
                  width={900}
                  height={1200}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3 Big Takeaways ── */}
      <section
        ref={takeawaysRef}
        id="takeaways"
        className="py-24 border-t border-[#e8e8e8]"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-sm uppercase tracking-[0.15em] text-[#6f6f6f] font-pp-neuebit mb-16">
              3 big takeaways
            </h2>

            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              {BIG_TAKEAWAYS.map((takeaway) => (
                <div key={takeaway.number} className="takeaway-card group">
                  {/* Number */}
                  <span className="block text-[56px] md:text-[72px] font-pp-neuebit font-bold text-[#e8e8e8] leading-none mb-4 group-hover:text-[#2D0456] transition-colors duration-500">
                    {takeaway.number}
                  </span>

                  {/* Title */}
                  <h3 className="text-xl md:text-2xl font-pp-acma font-bold text-[#111111] mb-4 leading-tight">
                    {takeaway.title}
                  </h3>

                  {/* Content */}
                  <p className="text-base text-[#6b6b6b] font-neue-regrade-variable leading-relaxed">
                    {takeaway.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Output (Stats) ── */}
      {projectData.output && (
        <ProjectOutput stats={projectData.output} id="output" />
      )}

      {/* ── External Resources ── */}
      <section
        ref={checkItOutRef}
        id="check-it-out"
        className="py-24 border-t border-[#e8e8e8]"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-sm uppercase tracking-[0.15em] text-[#6f6f6f] font-pp-neuebit mb-12">
              check it out
            </h2>

            {/* Full-width rows on small screens; three cards across the grid on desktop. */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
              {CHECK_IT_OUT_LINKS.map((item) => {
                const Icon = item.icon;

                return (
                  <a
                    key={item.title}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="check-it-out-link group relative flex items-center gap-4 rounded-lg border border-[#e8e8e8] bg-white p-5 text-[#111111] transition-colors duration-300 hover:border-[#2D0456] hover:text-[#2D0456] lg:min-h-[220px] lg:flex-col lg:items-start lg:justify-between lg:gap-10 lg:p-8"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#f4f4f5] transition-colors duration-300 group-hover:bg-[#2D0456] group-hover:text-white">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1 lg:w-full lg:flex-none">
                      <span className="block text-lg font-medium font-neue-regrade-variable">
                        {item.title}
                      </span>
                      <span className="block truncate text-sm text-[#6b6b6b] font-pp-neuebit">
                        {item.displayUrl}
                      </span>
                    </span>
                    <ArrowUpRight
                      className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 lg:absolute lg:right-8 lg:top-8"
                      aria-hidden="true"
                    />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── Next Project Footer ── */}
      <NextProjectFooter
        nextProject={
          nextProject
            ? {
                id: nextProject.id,
                title: nextProject.title,
                category: nextProject.category,
                accentColor: nextProject.accentColor,
              }
            : null
        }
      />
    </div>
  );
};

const AnimaliaPage = () => {
  const projectData = projects.find((p) => p.id === "animalia");

  if (!projectData?.password) {
    return <AnimaliaContent />;
  }

  return (
    <PasswordGate password={projectData.password} projectId="animalia">
      <AnimaliaContent />
    </PasswordGate>
  );
};

export default AnimaliaPage;
